import { Breadcrumb, Button, Card, Col, Empty, Icon, message, Row, Select, Table, Tabs } from 'antd';
import { gql } from 'apollo-boost';
import * as moment from 'moment';
import React, { useState } from 'react';
import { Mutation, Query } from 'react-apollo';
import * as CopyToClipboard from 'react-copy-to-clipboard';
import { Link } from 'react-router-dom';
import { Enrollment, EnrollmentGroup, LearnedLesson } from 'src/types/enrollment';
import { Statistics } from 'src/types/evaluation';
import { SpeechTypeText } from 'src/types/speech';
import { GTeacher } from 'src/types/teacher';
import { mobileMasking, nameMasking } from 'src/utils/masking';

import { EnrollmentReport } from '../../types/speech';

enum ReturnVisitStatus {
  VISITED,
  NOT_VISITED,
}

const ReturnVisitStatusText = {
  [ReturnVisitStatus.VISITED]: '已回访',
  [ReturnVisitStatus.NOT_VISITED]: '未回访',
};

export const RETURN_VISIT_MUTATION = gql`
  mutation returnVisit($enrollmentId: ID!) {
    returnVisit(enrollmentId: $enrollmentId) {
      id
    }
  }
`;

const ALL_TEACHERS_QUERY = gql`
  query getTeachersQuery {
    teachers {
      id
      name
    }
  }
`;

const GROUP_OPTIONS_QUERY = gql`
  query getTeachersQuery($ids: [ID]) {
    teachers(ids: $ids) {
      id
      enrollmentGroups {
        id
        name
        createdAt
        sku {
          id
          name
          start
          end
        }
      }
    }
  }
`;

const TEACHERS_QUERY = gql`
  query getEnrollmentGroups($ids: [ID], $teacherIds: [ID], $skuIds: [ID]) {
    enrollmentGroups(ids: $ids, teacherIds: $teacherIds, skuIds: $skuIds) {
      id
      name
      createdAt
      sku {
        id
        name
        start
        end
      }
      enrollments {
        id
        user {
          id
          name
          mobile
          family {
            children {
              name
              createdAt
            }
          }
        }
        returnVisit {
          newLearnedLessons {
            id
            serialId
            displayId
            name
          }
          lastVisitedLessons {
            id
            serialId
            displayId
            name
          }
          lastReturnVisitAt
        }
      }
    }
  }
`;

export const USERS_QUERY = gql`
  query GetUser($ids: [ID], $enrollmentIds: [Int], $speechDateRange: DateRange!, $lessonIds: [Int]) {
    users(ids: $ids) {
      id
      enrollments(ids: $enrollmentIds) {
        id
        enrollmentReport(speechDateRange: $speechDateRange, lessonIds: $lessonIds) {
          lessonNames
          knowledgePoints {
            name
            highestAccuracy
            allAccuracies
            lessonName
            speech {
              content
              type
            }
          }
        }
      }
    }
  }
`;

interface UserListData {
  key: number;
  mobile: string;
  name: string;
  childName: string;
  newLearnedLessons: LearnedLesson[];
  enrollment: Enrollment;
  groupName: string;
}

const UserList = (props: {
  groupsData?: UserListData[];
  isLearned?: boolean;
  groupExpandedRowKeys?: number[];
  setGroupExpandedRowKeys?: Function;
  selectedVisitStatusOption?: ReturnVisitStatus;
  selectedRowKeys?: number[];
  setSelectedRowKeys?: Function;
  currentPage: number;
  setCurrentPage: Function;
}) => {
  const {
    groupsData = [],
    isLearned,
    groupExpandedRowKeys,
    setGroupExpandedRowKeys,
    selectedVisitStatusOption,
    currentPage,
    setCurrentPage,
  } = props;

  const expandedRowRender = (record: UserListData) => {
    const lessonIds = record.newLearnedLessons.map(newLearnedLesson => newLearnedLesson.id);

    return (
      <Query
        query={USERS_QUERY}
        variables={{
          lessonIds,
          ids: [record.enrollment.user.id],
          enrollmentIds: [record.enrollment.id],
          speechDateRange: {
            from: moment().subtract(10, 'days').startOf('day').toDate(),
            to: moment().endOf('day').toDate(),
          },
        }}
      >
        {({ loading, error, data }) => {
          if (loading) {
            return 'loading...';
          }
          if (error || !data) {
            return 'error';
          }

          const [user] = data.users;
          if (!user) {
            // @ts-ignore
            return <Empty />;
          }

          const [enrollment] = user.enrollments;
          if (!enrollment) {
            // @ts-ignore
            return <Empty />;
          }

          const { knowledgePoints } = enrollment.enrollmentReport as EnrollmentReport;

          return (
            <Table dataSource={knowledgePoints} pagination={false} rowKey={() => Math.random().toString()}>
              <Table.Column width={'9%'} title="知识点" dataIndex="name" key="speechKnowledgeName" />
              <Table.Column<Statistics>
                sorter={(a, b) => {
                  if (a.highestAccuracy === 0) return 1; // Return 1 so that b goes first
                  if (b.highestAccuracy === 0) return -1; // Return -1 so that a goes first
                  return a.highestAccuracy - b.highestAccuracy;
                }}
                width={'9%'}
                title="最高正确率"
                dataIndex="highestAccuracy"
                key="speechHighestAccuracy"
                render={highestAccuracy => `${(highestAccuracy * 100).toFixed(0)}%`}
              />
              <Table.Column
                width={'9%'}
                title="所有正确率"
                dataIndex="allAccuracies"
                key="speechAllAccuracies"
                render={allAccuracies =>
                  allAccuracies.map((item: number, index: number) => {
                    let content = `${(item * 100).toFixed(0)}%`;
                    if (index < allAccuracies.length - 1) {
                      content += '-';
                    }
                    return content;
                  })
                }
              />
              <Table.Column width={'9%'} title="知识点对应课时" dataIndex="lessonName" key="lessonName" />
              <Table.Column
                width={'5%'}
                title="高/低"
                dataIndex="speech.type"
                key="speechType"
                render={type => SpeechTypeText[type]}
              />
              <Table.Column
                width={'50%'}
                title="内容"
                dataIndex="speech.content"
                key="speechContent"
                render={item => item || '-'}
              />
              <Table.Column
                width={'9%'}
                title="操作"
                dataIndex="speech.content"
                key="copy"
                render={speechContent => (
                  <CopyToClipboard text={speechContent || ''} onCopy={() => message.success('复制话术成功')}>
                    <Button style={{ padding: 5 }}>复制话术</Button>
                  </CopyToClipboard>
                )}
              />
            </Table>
          );
        }}
      </Query>
    );
  };

  const MainTable = (props: { children: React.ReactNode }) => {
    if (isLearned && selectedVisitStatusOption === ReturnVisitStatus.NOT_VISITED) {
      return (
        <Table
          style={{ marginTop: 20 }}
          expandedRowRender={expandedRowRender}
          dataSource={groupsData}
          rowKey={record => record.enrollment.id}
          expandedRowKeys={groupExpandedRowKeys || []}
          pagination={{ current: currentPage, onChange: page => setCurrentPage(page) }}
          onExpand={(expanded, record) => {
            if (!setGroupExpandedRowKeys || !groupExpandedRowKeys) {
              return;
            }

            if (expanded) {
              setGroupExpandedRowKeys([record.enrollment.id]);
            } else {
              setGroupExpandedRowKeys([]);
            }
          }}
        >
          {props.children}
        </Table>
      );
    }

    return (
      <Table style={{ marginTop: 20 }} dataSource={groupsData}>
        {props.children}
      </Table>
    );
  };

  return (
    <MainTable>
      <Table.Column
        title="手机号"
        key="mobile"
        render={record => {
          const maskedMobile = mobileMasking(record.enrollment.user.mobile);
          return (
            <Link to={`/user/${record.enrollment.user.id}`} target="_blank">
              {maskedMobile}
            </Link>
          );
        }}
      />
      <Table.Column title="微信昵称" dataIndex="name" key="name" />
      <Table.Column
        title="宝贝姓名"
        key="childName"
        render={record => {
          const maskedChildName = nameMasking(record.childName);
          return <>{maskedChildName}</>;
        }}
      />
      <Table.Column title="班级" dataIndex="groupName" key="groupName" />
      {isLearned && (
        <Table.Column
          title="新学习课程"
          dataIndex="newLearnedLessons"
          key="newLearnedLessons"
          render={newLearnedLessons => {
            return newLearnedLessons.map(
              (newLearnedLesson: { displayId: string; serialId: string }) =>
                `${newLearnedLesson.displayId || newLearnedLesson.serialId} `,
            );
          }}
        />
      )}
      <Table.Column
        title="操作"
        key="operation"
        render={record => (
          <Mutation mutation={RETURN_VISIT_MUTATION} variables={{ enrollmentId: record.enrollment.id }}>
            {createReturnVisit => {
              return (
                <CopyToClipboard
                  text={record.childName}
                  onCopy={async () => {
                    message.success('复制姓名成功');

                    const { returnVisit } = record;
                    const isVisited = !!(
                      returnVisit &&
                      returnVisit.lastReturnVisitAt &&
                      moment().isSame(returnVisit.lastReturnVisitAt, 'isoWeek')
                    );

                    !isVisited && (await createReturnVisit());
                  }}
                >
                  <Button type="primary">复制姓名</Button>
                </CopyToClipboard>
              );
            }}
          </Mutation>
        )}
      />
    </MainTable>
  );
};

const MainBreadcrumb = () => {
  return (
    <Breadcrumb style={{ marginBottom: 10, fontSize: 13 }}>
      <Breadcrumb.Item>
        <Link to={`/Home`}>首页</Link>
      </Breadcrumb.Item>
      <Breadcrumb.Item>用户回访</Breadcrumb.Item>
    </Breadcrumb>
  );
};

const EnrollmentGroupList = (props: {
  selectedTeacher: number | undefined;
  selectedSkuOption: number;
  selectedGroupOption: number;
  selectedVisitStatusOption: ReturnVisitStatus;
  groupExpandedRowKeys: number[];
  setGroupExpandedRowKeys: Function;
  currentPage: number;
  setCurrentPage: Function;
}) => {
  const {
    selectedTeacher,
    selectedSkuOption,
    selectedGroupOption,
    selectedVisitStatusOption,
    groupExpandedRowKeys,
    setGroupExpandedRowKeys,
    currentPage,
    setCurrentPage,
  } = props;

  const skuIds = selectedSkuOption ? [selectedSkuOption] : undefined;
  const enrollmentGroupIds = selectedGroupOption ? [selectedGroupOption] : undefined;

  return (
    <Query<{ enrollmentGroups: EnrollmentGroup[] }>
      query={TEACHERS_QUERY}
      variables={{
        skuIds,
        ids: enrollmentGroupIds,
        teacherIds: [selectedTeacher],
      }}
    >
      {({ loading, error, data }) => {
        if (loading) {
          return (
            <>
              <div style={{ textAlign: 'center' }}>
                <Icon type="loading" />
              </div>
            </>
          );
        }

        if (error || !data) {
          return 'error';
        }

        const { enrollmentGroups = [] } = data;

        const learnedUsers: {
          key: number;
          mobile: string;
          name: string;
          childName: string;
          newLearnedLessons: LearnedLesson[];
          enrollment: Enrollment;
          groupName: string;
        }[] = [];

        const notLearnedUsers: {
          key: number;
          mobile: string;
          name: string;
          childName: string;
          newLearnedLessons: LearnedLesson[];
          enrollment: Enrollment;
          groupName: string;
        }[] = [];

        for (const enrollmentGroup of enrollmentGroups) {
          for (const enrollment of enrollmentGroup.enrollments) {
            const { user, returnVisit } = enrollment;
            const { mobile, name } = user || {};

            if (!user) {
              continue;
            }

            const isVisited = !!(
              returnVisit &&
              returnVisit.lastReturnVisitAt &&
              moment().isSame(returnVisit.lastReturnVisitAt, 'isoWeek')
            );
            if (selectedVisitStatusOption === ReturnVisitStatus.VISITED && !isVisited) {
              continue;
            }

            if (selectedVisitStatusOption === ReturnVisitStatus.NOT_VISITED && isVisited) {
              continue;
            }

            let childName = '-';
            // 取最新的孩子信息
            if (user && user.family) {
              const [child] = user.family.children.sort((a, b) => {
                return moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf();
              });
              if (child) {
                childName = child.name;
              }
            }

            let newLearnedLessons: LearnedLesson[] = (returnVisit && returnVisit.newLearnedLessons) || [];

            if (isVisited) {
              newLearnedLessons = (returnVisit && returnVisit.lastVisitedLessons) || [];
            }

            if (newLearnedLessons.length > 0) {
              learnedUsers.push({
                newLearnedLessons,
                mobile,
                childName,
                name,
                enrollment,
                key: Number(enrollment.id),
                groupName: enrollmentGroup.name,
              });
            } else {
              notLearnedUsers.push({
                newLearnedLessons,
                mobile,
                childName,
                name,
                enrollment,
                key: Number(enrollment.id),
                groupName: enrollmentGroup.name,
              });
            }
          }
        }

        return (
          <Tabs defaultActiveKey="1">
            <Tabs.TabPane tab="已学习用户" key="1">
              <UserList
                groupsData={learnedUsers}
                isLearned
                selectedVisitStatusOption={selectedVisitStatusOption}
                groupExpandedRowKeys={groupExpandedRowKeys}
                setGroupExpandedRowKeys={setGroupExpandedRowKeys}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
              />
            </Tabs.TabPane>
            <Tabs.TabPane tab="未学习用户" key="2">
              <UserList
                groupsData={notLearnedUsers}
                isLearned={false}
                selectedVisitStatusOption={selectedVisitStatusOption}
                groupExpandedRowKeys={groupExpandedRowKeys}
                setGroupExpandedRowKeys={setGroupExpandedRowKeys}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
              />
            </Tabs.TabPane>
          </Tabs>
        );
      }}
    </Query>
  );
};

const GroupOptions = (props: {
  setSelectedSkuOption: Function;
  selectedSkuOption: number;
  selectedTeacher: number | undefined;
  selectedGroupOption: number;
  setSelectedGroupOption: Function;
  selectedVisitStatusOption: ReturnVisitStatus;
  groupExpandedRowKeys: number[];
  setGroupExpandedRowKeys: Function;
  currentPage: number;
  setCurrentPage: Function;
}) => {
  const {
    setSelectedSkuOption,
    selectedSkuOption,
    selectedTeacher,
    selectedGroupOption,
    setSelectedGroupOption,
    selectedVisitStatusOption,
    groupExpandedRowKeys,
    setGroupExpandedRowKeys,
    currentPage,
    setCurrentPage,
  } = props;

  if (!selectedTeacher) {
    return <UserList currentPage={currentPage} setCurrentPage={setCurrentPage} />;
  }

  return (
    <Query<{ teachers: GTeacher[] }>
      query={GROUP_OPTIONS_QUERY}
      variables={{
        ids: selectedTeacher,
      }}
    >
      {({ loading, error, data }) => {
        if (loading) {
          return (
            <>
              <div style={{ textAlign: 'center' }}>
                <Icon type="loading" />
              </div>
              <UserList currentPage={currentPage} setCurrentPage={setCurrentPage} />
            </>
          );
        }
        if (error || !data) {
          return 'error';
        }

        const { teachers } = data;

        const groupMap = new Map<string, { text: string; value: string }>();

        const skuMap = new Map<number, { text: string; value: number }>();
        for (const teacher of teachers) {
          for (const enrollmentGroup of teacher.enrollmentGroups || []) {
            skuMap.set(enrollmentGroup.sku.id, {
              text: enrollmentGroup.sku.name,
              value: enrollmentGroup.sku.id,
            });

            if (!selectedSkuOption || (selectedSkuOption && enrollmentGroup.sku.id === selectedSkuOption)) {
              groupMap.set(enrollmentGroup.id, { text: enrollmentGroup.name, value: enrollmentGroup.id });
            }
          }
        }

        const skuOptions: { text: string; value: number }[] = [...skuMap.values()];
        const groupOptions = [...groupMap.values()];

        return (
          <>
            <Row gutter={24} style={{ marginTop: 10 }}>
              <Col span={3} style={{ textAlign: 'right' }}>
                sku:
              </Col>
              <Col span={6} style={{ textAlign: 'left' }}>
                <Select
                  value={selectedSkuOption}
                  onChange={item => {
                    setSelectedSkuOption(item);
                    setSelectedGroupOption(undefined);
                  }}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    // @ts-ignore
                    option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: 300 }}
                >
                  {skuOptions.map(item => (
                    <Select.Option key={item.value} value={item.value}>
                      {item.text}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <Col span={3} style={{ textAlign: 'right' }}>
                班级:
              </Col>
              <Col span={12} style={{ textAlign: 'left' }}>
                <Select
                  value={selectedGroupOption}
                  onChange={item => {
                    setSelectedGroupOption(item);
                  }}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    // @ts-ignore
                    option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: 300 }}
                >
                  {groupOptions.map(item => (
                    <Select.Option key={item.value} value={item.value}>
                      {item.text}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
            </Row>
            <EnrollmentGroupList
              selectedTeacher={selectedTeacher}
              selectedSkuOption={selectedSkuOption}
              selectedGroupOption={selectedGroupOption}
              selectedVisitStatusOption={selectedVisitStatusOption}
              groupExpandedRowKeys={groupExpandedRowKeys}
              setGroupExpandedRowKeys={setGroupExpandedRowKeys}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </>
        );
      }}
    </Query>
  );
};

const ReturnVisit = () => {
  const [selectedVisitStatusOption, setSelectedVisitStatusOption] = useState(ReturnVisitStatus.NOT_VISITED);

  const visitStatusOptions = [
    {
      text: ReturnVisitStatusText[ReturnVisitStatus.VISITED],
      value: ReturnVisitStatus.VISITED,
    },
    {
      text: ReturnVisitStatusText[ReturnVisitStatus.NOT_VISITED],
      value: ReturnVisitStatus.NOT_VISITED,
    },
  ];

  const [selectedTeacher, setSelectedTeacher] = useState<number>();

  const [selectedSkuOption, setSelectedSkuOption] = useState();
  const [selectedGroupOption, setSelectedGroupOption] = useState();

  const [groupExpandedRowKeys, setGroupExpandedRowKeys] = useState<number[]>([]);

  const [currentPage, setCurrentPage] = useState<number>(1);

  return (
    <>
      {MainBreadcrumb()}
      <Query<{ teachers: GTeacher[] }> query={ALL_TEACHERS_QUERY}>
        {({ loading, error, data }) => {
          if (loading) {
            return 'loading...';
          }
          if (error || !data) {
            return 'error';
          }

          const { teachers } = data;
          const teacherOptions = teachers.map(teacher => {
            return { text: teacher.name, value: teacher.id };
          });

          return (
            <Card title="用户回访">
              <Row gutter={24}>
                <Col span={3} style={{ textAlign: 'right' }}>
                  本周回访状态:
                </Col>
                <Col span={6} style={{ textAlign: 'left' }}>
                  <Select
                    value={selectedVisitStatusOption}
                    onChange={item => {
                      setSelectedVisitStatusOption(Number(item));
                    }}
                    style={{ width: 80 }}
                  >
                    {visitStatusOptions.map(item => (
                      <Select.Option key={item.value} value={item.value}>
                        {item.text}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
                <Col span={3} style={{ textAlign: 'right' }}>
                  班主任:
                </Col>
                <Col span={12} style={{ textAlign: 'left' }}>
                  <Select
                    defaultValue={selectedTeacher}
                    onChange={item => {
                      setSelectedTeacher(Number(item));
                      setSelectedSkuOption(undefined);
                      setSelectedGroupOption(undefined);
                    }}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      // @ts-ignore
                      option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    style={{ width: 300 }}
                  >
                    {teacherOptions.map(item => (
                      <Select.Option key={item.value} value={item.value}>
                        {item.text}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
              </Row>

              <GroupOptions
                setSelectedSkuOption={setSelectedSkuOption}
                selectedSkuOption={selectedSkuOption}
                selectedTeacher={selectedTeacher}
                selectedGroupOption={selectedGroupOption}
                setSelectedGroupOption={setSelectedGroupOption}
                selectedVisitStatusOption={selectedVisitStatusOption}
                groupExpandedRowKeys={groupExpandedRowKeys}
                setGroupExpandedRowKeys={setGroupExpandedRowKeys}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
              />
            </Card>
          );
        }}
      </Query>
    </>
  );
};

export { ReturnVisit };



// WEBPACK FOOTER //
// ./src/screens/returnVisit/ReturnVisit.tsx